import { observable, action, computed, configure, runInAction } from 'mobx';
import { computedFn } from 'mobx-utils';
import { create, persist } from 'mobx-persist';
import { primitive } from 'serializr';
import centroid from '@turf/centroid';
import { zip } from 'lodash';

import {
  fetchPopulationData,
  fetchGeoData,
  fetchPOIData,
  fetchCityData
} from './fetch';
import { getKarteTemplate } from '../template';
import {
  Karte,
  PopulationDataType,
  CityDataType,
  Format,
  PopulationData,
  Category,
  ValueType,
  City,
  SheetFormat,
  CityData,
  GeoData,
  POIData
} from '../types';
import {
  getAmountData,
  getOldestData,
  getProportionData,
  getValue,
  getValues,
  getAreaName,
  getLegendData
} from '../util';
import { LatLngExpression } from 'leaflet';

configure({ enforceActions: 'always' });

const karteSchema = {
  factory: () => ({}),
  props: {
    category: primitive(),
    area: primitive(),
    city: primitive(),
    populationDataType: primitive(),
    cityDataType: primitive(),
    valueType: primitive(),
    year: primitive(),
    format: primitive(),
    support: primitive()
  }
};

export default class KarteStore {
  @persist
  @observable
  isShowingDrawer = false;

  @persist
  @observable
  divisionCount = 1;

  @persist
  @observable
  sheetFormat: SheetFormat = SheetFormat.Template;

  @persist
  @observable
  selectedCityFortemplate: City = City.Nakahara;

  @persist
  @observable
  isShowingSheetSelection = true;

  @persist('list', karteSchema)
  @observable
  kartes: Karte[] = new Array(4).fill({});

  @observable
  template: Karte[][] = [];

  @persist
  @observable
  numberOfCurrentTemplatePage: number = 0;

  @persist
  @observable
  isShowingAdditionalModal = false;

  @persist
  @observable
  numberOfAdditionalDivision = 0;

  @observable
  isFetching = false;

  @observable
  POIData: POIData | undefined;

  @observable.shallow
  populationData: { [key in Category]?: PopulationData[] } = {};

  @observable.shallow
  geoData: { [key in Category]?: GeoData } = {};

  @observable.shallow
  cityData: { [key in CityDataType]?: CityData[] } = {};

  @action
  async fetchAreas(c: Category) {
    if (this.populationData[c]) return;
    this.isFetching = true;

    const data = await fetchPopulationData(c);

    runInAction(() => {
      this.populationData[c] = data;
      this.isFetching = false;
    });
  }

  @action
  async fetchLackData() {
    this.isFetching = true;

    const lackData = this.lackData;
    try {
      const [cityData, geoData, populationData, poiData] = await Promise.all([
        Promise.all(lackData.cityData.map(args => fetchCityData(args))),
        Promise.all(lackData.geoData.map(args => fetchGeoData(args))),
        Promise.all(
          lackData.populationData.map(args => fetchPopulationData(args))
        ),
        lackData.poiData ? fetchPOIData() : undefined
      ]);

      runInAction(() => {
        cityData.forEach((d, i) => {
          const dt = lackData.cityData[i];
          this.cityData[dt] = d;
        });
        geoData.forEach((d, i) => {
          const category = lackData.geoData[i];
          this.geoData[category] = d;
        });
        populationData.forEach((d, i) => {
          const category = lackData.populationData[i];
          this.populationData[category] = d;
        });
        if (poiData) {
          this.POIData = poiData;
        }
        this.isFetching = false;
      });
    } catch (e) {
      runInAction(() => {
        this.isFetching = false;
      });
      throw e;
    }
  }

  // 不足しているデータのキーを取得します
  @computed
  get lackData() {
    const cityDataTypes = this.kartes
      .map(k => (k.format === Format.BarChart ? k.cityDataType : undefined))
      .filter((k): k is CityDataType => !!k);
    const populationCategories = this.kartes
      .map(k => (k.format !== Format.BarChart ? k.category : undefined))
      .filter((k): k is Category => !!k);
    const geoCategories = this.kartes
      .map(k =>
        k.format === Format.Map || k.format === Format.Similarity
          ? k.category
          : undefined
      )
      .filter((k): k is Category => !!k);
    const poiData = this.kartes.some(
      k => k.format === Format.Map || k.format === Format.Similarity
    );

    if (this.kartes.some(k => k.valueType === ValueType.Proportion)) {
      populationCategories.push(Category.Ku);
    }

    const loadedCityData = (Object.keys(
      this.cityData
    ) as CityDataType[]).filter(k => !!this.cityData[k]);
    const loadedGeoData = (Object.keys(this.geoData) as Category[]).filter(
      k => !!this.geoData[k]
    );
    const loadedPopulationData = (Object.keys(
      this.populationData
    ) as Category[]).filter(k => !!this.populationData[k]);

    return {
      populationData: Array.from(
        new Set(
          populationCategories.filter(c => !loadedPopulationData.includes(c))
        )
      ),
      geoData: Array.from(
        new Set(geoCategories.filter(c => !loadedGeoData.includes(c)))
      ),
      cityData: Array.from(
        new Set(cityDataTypes.filter(c => !loadedCityData.includes(c)))
      ),
      poiData
    };
  }

  @action.bound
  selectCityForTemplate(city: City) {
    this.selectedCityFortemplate = city;
  }

  @action.bound
  showSheetSelection() {
    this.isShowingSheetSelection = true;
    this.isShowingAdditionalModal = false;
  }

  @action.bound
  closeSheetSelection() {
    this.isShowingSheetSelection = false;
  }

  @action.bound
  changeSheetFormat(sheetFormat: SheetFormat) {
    this.sheetFormat = sheetFormat;
    this.isShowingDrawer = sheetFormat !== SheetFormat.Template;
    if (sheetFormat === SheetFormat.Template) {
      this.getKarteTemplate();
      this.addTemplateKartes(0);
    }
  }

  @action.bound
  toggleDrawer() {
    this.isShowingDrawer = !this.isShowingDrawer;
  }

  @action.bound
  changeDivision(num: number) {
    this.divisionCount = num;
  }

  @action.bound
  showAdditionalModal(divisionNum: number) {
    this.isShowingAdditionalModal = true;
    this.numberOfAdditionalDivision = divisionNum;
  }

  @action.bound
  closeAdditionalModal() {
    this.isShowingAdditionalModal = false;
  }

  @action.bound
  prevTemplateSheetPage() {
    if (this.numberOfCurrentTemplatePage <= 0) return;
    this.numberOfCurrentTemplatePage--;
    this.addTemplateKartes(this.numberOfCurrentTemplatePage);
  }

  @action.bound
  nextTemplateSheetPage() {
    if (this.numberOfCurrentTemplatePage >= this.template.length - 1) return;
    this.numberOfCurrentTemplatePage++;
    this.addTemplateKartes(this.numberOfCurrentTemplatePage);
  }

  @action.bound
  addTemplateKartes(pageNumber: number) {
    this.template[pageNumber].forEach((t, i) => this.addKarte(i, t));
    this.numberOfCurrentTemplatePage = pageNumber;
    this.divisionCount = this.template[pageNumber].length;
    this.fetchLackData();
  }

  @action.bound
  // ステートの変更などがあったときに、これが呼び出されてバインドされたアクションが実行される
  addKarte(divisionNum: number, k: Karte) {
    const karte = { ...k };
    this.kartes[divisionNum] = karte;
    this.fetchLackData();
  }

  @action.bound
  removeKarte(divisionNum: number) {
    const resetKarte: Karte[] = new Array(4).fill({
      category: undefined,
      area: undefined,
      dataType: undefined,
      valueType: undefined,
      year: undefined,
      format: undefined,
      support: undefined
    });
    this.kartes.forEach((c, i) => {
      if (i === divisionNum) return;
      resetKarte[i] = c;
    });
    this.kartes = resetKarte;
  }

  @action.bound
  getKarteTemplate() {
    this.template = getKarteTemplate(this.selectedCityFortemplate);
  }

  getKarteMapState = computedFn((num: number) => {
    const karte = this.kartes[num];

    const pos = this.getGeoCenter(karte);
    const position: LatLngExpression | undefined =
      pos && typeof pos[0] === 'number' && typeof pos[1] === 'number'
        ? { lat: pos[1], lng: pos[0] }
        : undefined;

    return {
      karte,
      selectedAreaName: undefined,
      legends: getLegendData(
        karte.category,
        karte.populationDataType,
        karte.valueType,
        karte.format
      ),
      geoData: this.getGeoData(karte),
      poiData: this.POIData,
      values: this.getValues(karte),
      position
    };
  });

  getAreas = computedFn((c?: Category, year?: number, city?: City) => {
    if (!c || !year || !city) return;
    const data = this.populationData[c];
    if (!data) return;
    const areas = data
      .filter(d => d.year === year && d.cityName === city)
      .map(d => (c === Category.Ku ? d.cityName : d.areaName));
    return Array.from(new Set(areas));
  });

  filteredAreas = computedFn(
    (category: Category, area: string, year?: number) => {
      return this.populationData[category]?.filter(
        d =>
          (category === Category.Ku ? d.cityName : d.areaName) === area &&
          (!year || d.year === year)
      );
    }
  );

  getChartData = computedFn((index: number) => {
    const karte = this.kartes[index];
    const {
      category,
      area,
      populationDataType: dataType,
      valueType,
      support
    } = karte;
    // 　　2030年2040年が前に来ていたので、年で昇順
    const filteredAreaData = this.filteredAreas(category, area)?.sort(
      (a, b) => a.year - b.year
    );
    if (!filteredAreaData?.length) return { karte };
    const years = new Set(filteredAreaData.map(d => d.year));
    const areaName =
      category === Category.Ku
        ? filteredAreaData[0].cityName
        : filteredAreaData[0].areaName;

    return {
      karte,
      data: Array.from(years.keys()).map(y => ({
        year: y,
        [areaName]: !dataType
          ? undefined
          : valueType === ValueType.Amount
          ? getAmountData(
              filteredAreaData.filter(d => d.year === y)[0],
              dataType,
              support
            )
          : valueType === ValueType.Proportion
          ? getProportionData(
              filteredAreaData.filter(d => d.year === y)[0],
              dataType,
              getOldestData(filteredAreaData, category, area, dataType)
            )
          : undefined,
        ...(valueType === ValueType.Proportion && dataType
          ? {
              川崎市:
                dataType === PopulationDataType.Support
                  ? undefined
                  : this.getEntireCityProportionData(y, dataType),
              [filteredAreaData[0].cityName]: this.getCityProportionData(
                y,
                dataType,
                filteredAreaData[0].cityName
              )
            }
          : {})
      }))
    };
  });

  getKarte = computedFn((num: number) => this.kartes[num]);

  getCityData = computedFn((num: number) => {
    const karte = this.getKarte(num);
    return {
      city: karte.city,
      cityDataType: karte.cityDataType,
      data: karte.cityDataType ? this.cityData[karte.cityDataType] : undefined
    };
  });

  // 類似地域のサマリーテキストと、類似度Top3エリアを取得する
  getSimilarityCluster = computedFn((karteIndex: number, area?: string) => {
    const karte = this.kartes[karteIndex];
    const areaName = area ?? karte.area;

    // 同一カテゴリ・年度のデータに絞る
    const populationData = this.populationData[karte.category]?.filter(
      d => d.year === karte.year
    );

    const similarity = populationData
      // エリアを絞る
      ?.filter(
        d =>
          (karte.category === Category.Ku ? d.cityName : d.areaName) ===
            areaName && d.year === karte.year
      )
      // 同名エリアが存在する場合があるため、最初にヒットしたデータを使用する。
      .find(d => d.similarity.similarAreaSummaryText)?.similarity;

    // 類似地域トップ3は１つの文字列になっているため、分割
    const top3AreaName = similarity?.top3SimilarAreaName.split('_');

    // 現在選択されているエリアの類似地域クラスター番号を取得する
    const sindex = similarity?.similarClusterIndex;

    const filteredAreaList = (sindex ? populationData : undefined)
      // 現在選択中のエリアと同じ類似地域クラスター番号のオブジェクトに絞る
      ?.filter(d => d.similarity.similarClusterIndex === sindex)
      // オブジェクトからエリア名に変更
      .map(d => d.areaName);
    // 名前が重複している可能性があるため、セットにして重複をなくす
    const similarityAreas = Array.from(new Set(filteredAreaList));

    return { similarity, top3AreaName, similarityAreas };
  });

  getGeoData = computedFn((karte: Karte) => {
    return this.geoData[karte.category];
  });

  getGeoCenter = computedFn((karte: Karte) => {
    const feature = this.getGeoData(karte)?.features?.find(
      f => getAreaName(f.properties, karte.category) === karte.area
    );
    return feature ? centroid(feature).geometry.coordinates : undefined;
  });

  getPopulationData = computedFn((karte: Karte) => {
    return this.populationData[karte.category];
  });

  getValues = computedFn((karte: Karte) => {
    return getValues(
      this.getGeoData(karte),
      this.getPopulationData(karte),
      karte
    );
  });

  private getValue = (
    karte: Karte,
    area?: string,
    dataType?: PopulationDataType
  ) => {
    return getValue(this.getPopulationData(karte), karte, area, dataType);
  };

  getRanking = computedFn((karteIndex: number) => {
    const karte = this.kartes[karteIndex];
    if (karte.valueType === ValueType.Proportion || !karte.populationDataType) {
      return undefined;
    }

    // カテゴリ別のデータ（区、や小学校、中学校など）
    const populationData = this.populationData[karte.category];
    if (!populationData) {
      return undefined;
    }

    // 以下で年度、値のタイプなどを絞っている。
    const yearFilteredData = populationData.filter(d => d.year === karte.year);

    // カテゴリーが区かどうかで条件が変わる
    const isKu = karte.category === Category.Ku;
    const isTotalData = karte.populationDataType === PopulationDataType.Total;

    // 全地域のエリア名を格納（重複があるためセットにする）
    const allAreaNameList = Array.from(
      new Set(yearFilteredData.map(d => d.areaName))
    );

    return (areaName: string = karte.area) => {
      // 選択中のエリアの値を算出
      const selfAreaValue = this.getValue(karte, areaName);
      if (typeof selfAreaValue !== 'number') {
        return undefined;
      }

      // cityはステートとして変化しないため、この関数が呼び出されるたびにCityを内部的に更新する
      const selectedCityName = populationData.find(d => d.areaName === areaName)
        ?.cityName;

      // 区内のエリアからそのエリアの値の配列を取得する
      const areaValueList = yearFilteredData
        .filter(d =>
          isKu ? d.cityName !== '川崎市' : d.cityName === selectedCityName
        )
        .map(d => this.getValue(karte, isKu ? d.cityName : d.areaName))
        .filter((d): d is number => typeof d === 'number');

      // 上の段階でSortしてしまうと、この後の人口あたりのランキングを出す際に、やりにくいためここではSort済みと、そうでないもので別オブジェクトとして管理
      const sortedAreaValue = areaValueList.slice().sort((a, b) => b - a);
      // 全地域のエリア名リストから、各エリアの値を算出する
      const allAreaValueList = allAreaNameList.map(areaName =>
        this.getValue(karte, areaName)
      );
      const sortedAllAreaValue = allAreaValueList
        .slice()
        .sort((a: any, b: any) => b - a);

      // データが総人口以外の場合は人口あたりのランキングも算出
      let rankingInKuPerPopulation: number | undefined;
      let rankingInCityPerPopulation: number | undefined;
      if (!isTotalData) {
        // 選択中のエリアの人口を算出
        const selfAreaPopulation = this.getValue(
          karte,
          areaName,
          PopulationDataType.Total
        );

        if (selfAreaPopulation) {
          // 総人口データを取得
          const areaPopulationList = yearFilteredData
            // カテゴリが区の場合は、川崎市本体を除外、カテゴリがそれ以外の場合は、その区のみに絞り込む
            .filter(
              isKu
                ? d => d.cityName !== '川崎市'
                : d => d.cityName === selectedCityName
            )
            // 人口データを取得
            .map(
              o =>
                this.getValue(
                  karte,
                  isKu ? o.cityName : o.areaName,
                  PopulationDataType.Total
                ) ?? 0
            );

          // 選択中のエリアの人口あたりの比率　例：人口あたりの年少人口の割合
          const selfAreaValuePerPopulation = selfAreaValue / selfAreaPopulation;

          rankingInKuPerPopulation =
            zip(areaValueList, areaPopulationList)
              .map(
                // データを人口データで割る
                ([v, v2]) =>
                  // 分母が0の場合は、0を返せば、Sortしたときに必ず最後に来る
                  v && v2 ? v / v2 : 0
              )
              .sort((a, b) => b - a)
              .indexOf(selfAreaValuePerPopulation) + 1;

          if (!isKu) {
            // カテゴリが区以外の場合に、川崎市内各エリアの人口データリストを取得
            const allAreaPopulationlist = allAreaNameList.map(
              areaName =>
                this.getValue(karte, areaName, PopulationDataType.Total) ?? 0
            );

            rankingInCityPerPopulation =
              zip(allAreaValueList, allAreaPopulationlist)
                .map(([v, v2]) => (v && v2 ? v / v2 : 0))
                .sort((a, b) => b - a)
                .indexOf(selfAreaValuePerPopulation) + 1;
          }
        }
      }

      return {
        rankingInKu: sortedAreaValue.indexOf(selfAreaValue) + 1,
        // カテゴリが区の場合、データが総人口かそれ以外のデータかのそれぞれで条件分岐
        rankingInCity: isKu
          ? undefined
          : sortedAllAreaValue.indexOf(selfAreaValue) + 1,
        rankingInKuPerPopulation,
        rankingInCityPerPopulation,
        kuCount: sortedAreaValue.length,
        cityCount: sortedAllAreaValue.length,
        selectedCityName: selectedCityName
      };
    };
  });

  getCityProportionData = computedFn(
    (year: number, dataType: PopulationDataType, cityName: string) => {
      const data = this.populationData[Category.Ku];
      if (!data) return;

      const years = new Set(data.map(d => d.year));
      let comparison, amount;
      data.forEach(d => {
        if (d.cityName === cityName && d.year === year) {
          amount = getAmountData(d, dataType);
        }
        if (d.cityName === cityName && d.year === Array.from(years.keys())[0]) {
          comparison = getAmountData(d, dataType);
        }
      });
      if (!amount || !comparison) return undefined;
      return Math.floor((amount / comparison) * 100);
    }
  );

  getEntireCityProportionData = computedFn(
    (year: number, dataType: PopulationDataType) => {
      const data = this.populationData[Category.Ku];
      if (!data) return;
      const years = new Set(data.map(d => d.year));
      const comparison = this.getEntireCityAmountData(
        data,
        Array.from(years.keys())[0],
        dataType
      );
      const amount = this.getEntireCityAmountData(data, year, dataType);
      return amount ? Math.floor((amount / comparison) * 100) : undefined;
    }
  );

  getEntireCityAmountData = computedFn(
    (
      citydata: PopulationData[],
      year: number,
      dataType: PopulationDataType
    ) => {
      return citydata
        .filter(d => d.year === year)
        .reduce((a, b) => a + getAmountData(b, dataType), 0);
    }
  );

  @action.bound
  resetCustomizeSettings = () => {
    this.changeDivision(1);
    this.removeKarte(0);
    this.removeKarte(1);
    this.removeKarte(2);
    this.removeKarte(3);
    this.isShowingDrawer = true;
  };
}

export const hydrate = create();
